<template>
  <div>
    <v-card class="mb-5">
      <v-card-text>
        <h3>
          {{ $t('Training Management') }}
        </h3>

        <v-data-table
          :headers="trainingDataTableHeaders"
          :items="usersTrainingData || []"
          calculate-widths
          show-expand
          item-key="years"
        >
          <template v-slot:top>
            <div class="d-flex">
              <v-btn
                class="ml-auto"
                color="secondary"
                @click="exportTrainingTable"
              >
                {{ $t('export') }}
              </v-btn>
            </div>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td
              class="pa-2"
              :colspan="headers.length"
            >
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">
                        {{ $t('Laboratory') }}
                      </th>
                      <th class="text-left">
                        {{ $t('Manager') }}
                      </th>
                      <th class="text-left">
                        {{ $t('Address') }}
                      </th>
                      <th class="text-left">
                        {{ $t('Telephone') }}
                      </th>
                      <th class="text-left" />
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="lab in item.labs"
                      :key="lab.id + item.years"
                    >
                      <td>{{ lab.name }}</td>

                      <td>
                        <span v-if="lab.manager">
                          {{ lab.manager.firstName }} {{ lab.manager.lastName }}
                        </span>
                      </td>
                      <td>{{ lab.address }}</td>
                      <td>{{ lab.telephone }}</td>

                      <td>
                        <v-btn
                          icon
                          :to="{name: 'LaboratoryView', params: {laboratoryId: lab.id}}"
                          target="_blank"
                        >
                          <v-icon
                            small
                          >
                            mdi-open-in-new
                          </v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-text>
        <h3>
          {{ $t('Top Training Modules') }}
        </h3>

        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t('Training Module') }}
                </th>
                <th class="text-left">
                  {{ $t('Number of individuals trained') }}
                </th>
                <th class="text-left">
                  {{ $t('Module Session and Number of individuals trained') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="module in topModules"
                :key="module.id"
              >
                <td> {{ module.name }}</td>
                <td> {{ module.trainedUsers }}</td>
                <td>
                  <div
                    v-for="(session, i) in module.sessions"
                    :key="session.id"
                  >
                    {{ session.dateRange.split(',').join(' ~ ') }} / {{ session.users.length }}
                    <v-divider v-if="i < module.sessions.length - 1" />
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
    import moment from 'moment'
    import {
        export_json_to_excel_custom
    } from '@/vendor/Export2Excel'

    export default {
        name: "LaboratoryTrainingStatistics",

        props: {
            laboratories: {
                type: [Array, Boolean],
                required: false,
                default: () => false,
            },
            laboratory: {
                type: [Object, Boolean],
                required: false,
                default: () => false,
            },
            trainingSessions: {
                type: Array,
                required: true
            },
            trainingModules: {
                type: Array,
                required: true
            },
        },

        data: () => {
            return {}
        },

        computed: {
            trainingDataTableHeaders() {
                return [
                    {
                        text: this.$t('Not trained since more than'),
                        value: 'years',
                        align: 'start',
                        sortable: false,
                    },
                    {
                        text: this.$t('Number / % of individuals'),
                        value: 'statistic',
                        align: 'start',
                        sortable: false,
                    },
                    {
                        text: '',
                        value: 'data-table-expand'
                    },
                ]
            },

            labUsers() {
                if (this.laboratory) {
                    return this.laboratory?.users || [];
                } else if (this.laboratories) {
                    let users = [];
                    this.laboratories.forEach(lab => {
                        users.push(...lab.users)
                    });
                    return users
                }

                return []
            },

            usersTrainingData() {
                return this.sortUsersToTrainingData();
            },

            topModules() {
                return this.joinModuleToSessionToUser()
            }
        },

        methods: {
            sortUsersToTrainingData() {
                // let currentDate = moment();
                let oneYear = moment(moment().subtract(1, 'years'));
                let twoYear = moment(moment().subtract(2, 'years'));
                let treeYear = moment(moment().subtract(3, 'years'));

                let oneYearTrained = {users: [], percent: '', labs: {}};
                let twoYearTrained = {users: [], percent: '', labs: {}};
                let treeYearTrained = {users: [], percent: '', labs: {}};
                let neverTrained = {users: [], percent: '', labs: {}};

                for (let user of this.labUsers) {
                    let metadata = user.metadata;

                    let oneLab = !!this.laboratory;


                    if (!metadata?.training?.length) {
                        neverTrained.users.push(user);

                        if (oneLab) {
                            if (!neverTrained.labs[this.laboratory.id]) {
                                neverTrained.labs[this.laboratory.id] = this.laboratory
                            }
                        } else {
                            if (!neverTrained.labs[user.laboratory]) {
                                neverTrained.labs[user.laboratory] = this.laboratories.find(l => l.id === user.laboratory)
                            }
                        }

                        continue;
                    }

                    let lastTraining = metadata.training[metadata.training.length - 1];

                    let trSession = this.trainingSessions.find(s => s.id == lastTraining.trainingSessionId);
                    let lastTrainingDate = moment(trSession.dateRange.split(', ')[1]);


                    if (lastTrainingDate.isAfter(oneYear)) {
                        oneYearTrained.users.push(user);

                        if (oneLab) {
                            if (!oneYearTrained.labs[this.laboratory.id]) {
                                oneYearTrained.labs[this.laboratory.id] = this.laboratory
                            }
                        } else {
                            if (!oneYearTrained.labs[user.laboratory]) {
                                oneYearTrained.labs[user.laboratory] = this.laboratories.find(l => l.id === user.laboratory)
                            }
                        }

                    } else if (lastTrainingDate.isAfter(twoYear)) {
                        twoYearTrained.users.push(user);

                        if (oneLab) {
                            if (!twoYearTrained.labs[this.laboratory.id]) {
                                twoYearTrained.labs[this.laboratory.id] = this.laboratory
                            }
                        } else {
                            if (!twoYearTrained.labs[user.laboratory]) {
                                twoYearTrained.labs[user.laboratory] = this.laboratories.find(l => l.id === user.laboratory)
                            }
                        }
                    } else if (lastTrainingDate.isAfter(treeYear)) {
                        treeYearTrained.users.push(user);

                        if (oneLab) {
                            if (!treeYearTrained.labs[this.laboratory.id]) {
                                treeYearTrained.labs[this.laboratory.id] = this.laboratory
                            }
                        } else {
                            if (!treeYearTrained.labs[user.laboratory]) {
                                treeYearTrained.labs[user.laboratory] = this.laboratories.find(l => l.id === user.laboratory)
                            }
                        }

                    }
                }

                let labUsersLength = this.labUsers.length;

                oneYearTrained.percent = ((oneYearTrained.users.length * 100) / labUsersLength).toFixed(2);
                twoYearTrained.percent = ((twoYearTrained.users.length * 100) / labUsersLength).toFixed(2);
                treeYearTrained.percent = ((treeYearTrained.users.length * 100) / labUsersLength).toFixed(2);
                neverTrained.percent = ((neverTrained.users.length * 100) / labUsersLength).toFixed(2);

                return [
                    {
                        years: this.$t('1 year'),
                        statistic: `${oneYearTrained.users.length} / ${oneYearTrained.percent} %`,
                        labs: Object.values(oneYearTrained.labs)
                    },
                    {
                        years: this.$t('2 years'),
                        statistic: `${twoYearTrained.users.length} / ${twoYearTrained.percent} %`,
                        labs: Object.values(twoYearTrained.labs)
                    },
                    {
                        years: this.$t('3 years'),
                        statistic: `${treeYearTrained.users.length} / ${treeYearTrained.percent} %`,
                        labs: Object.values(treeYearTrained.labs)
                    },
                    {
                        years: this.$t('never'),
                        statistic: `${neverTrained.users.length} / ${neverTrained.percent} %`,
                        labs: Object.values(neverTrained.labs)
                    }
                ]
            },

            getLastSessions() {
                let yearLimit = moment().subtract(2, 'years');
                return this.trainingSessions.filter(s => moment(s.dateRange.split(', ')[1]).isAfter(yearLimit, 'year'));
            },

            joinModuleToSessionToUser() {
                let sessions = JSON.parse(JSON.stringify(this.getLastSessions()));
                let modules = JSON.parse(JSON.stringify(this.trainingModules));

                sessions.forEach(session => {
                    let module = modules.find(e => e.id === session.trainingModule);
                    module.trainedUsers = 0;
                    session.module = module;
                    if (!Array.isArray(module.sessions)) {
                        module.sessions = []
                    }
                    module.sessions.push(session)
                });

                this.labUsers.forEach(user => {
                    if (user?.metadata?.training) {
                        user?.metadata?.training.forEach(t => {
                            if (t.status) {
                                let userSession = sessions.find(e => e.id == t.trainingSessionId);
                                if (userSession) {
                                    if (!Array.isArray(userSession.users)) {
                                        userSession.users = []
                                    }
                                    userSession.users.push(user);
                                    userSession.module.trainedUsers++
                                }
                            }
                        })
                    }
                });

                modules = modules.sort((a, b) => b.trainedUsers - a.trainedUsers).slice(0, 5);

                return modules
            },

            findSession(sessionId) {
                return this.trainingSessions.find(e => e.id === sessionId)
            },

            findModule(moduleId) {
                return this.trainingModules.find(e => e.id === moduleId)
            },

            exportTrainingTable() {
                let multiData = [];

                {
                    const tHeader = [
                        'Not trained since more than', 'Number / % of individuals'
                    ];


                    const filterVal = [
                        'years', 'statistic'
                    ];


                    const data = this.usersTrainingData.map(v => {
                        let filteredData = filterVal.map(j => {
                            if (v[j]) {
                                return v[j].toString()
                            }
                            return ''
                        });

                        return filteredData;
                    }).filter(e => e);

                    multiData.push({name: 'Statistic', header: tHeader, data})
                }

                {
                    const tHeader = [
                        '#', 'Id', 'Name', 'Address', 'Manager', 'Telephone'
                    ];


                    const filterVal = [
                        '#', 'id', 'name', 'address', 'manager', 'telephone'
                    ];

                    const deepValues = {
                        manager(item) {
                            return item?.manager?.firstName + '' + item?.manager?.lastName || ''
                        }
                    };

                    for (let s of this.usersTrainingData) {
                        const data = s.labs.map((v, index) => {
                            return filterVal.map(j => {
                                if (j === '#') return index + 1;
                                if (v[j]) {
                                    if (j in deepValues) {
                                        return deepValues[j](v)
                                    } else {
                                        return v[j].toString()
                                    }
                                }

                                return ''
                            });
                        }).filter(e => e);

                        multiData.push({name: s.years, header: tHeader, data})

                    }
                }

                export_json_to_excel_custom({
                    multiData,
                    filename: 'training-statistic',
                    autoWidth: true,
                })

            },
        }

    }
</script>

<style scoped>

</style>
